import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';

const PAGES = ['home', 'mission', 'projects', 'volunteer', 'donate', 'contact'];
const SECTIONS: Record<string, string[]> = {
  home: ['hero', 'mission', 'impact', 'projects', 'volunteer', 'donate'],
  mission: ['hero', 'vision', 'values', 'team'],
  projects: ['hero', 'list'],
  volunteer: ['hero', 'opportunities'],
  donate: ['hero', 'tiers', 'impact'],
  contact: ['hero', 'form', 'info'],
};
const LANGUAGES = [
  { code: 'ps', label: 'Pashto', flag: '🇦🇫' },
  { code: 'da', label: 'Dari', flag: '🇦🇫' },
];

interface CmsEntry {
  id: string;
  page: string;
  section: string;
  language: string;
  content: Record<string, string>;
  updated_at: string;
}

export default function AdminContentPage() {
  const [activeLang, setActiveLang] = useState('ps');
  const [activePage, setActivePage] = useState('home');
  const [activeSection, setActiveSection] = useState('hero');
  const [entries, setEntries] = useState<CmsEntry[]>([]);
  const [form, setForm] = useState<Record<string, string>>({ title: '', subtitle: '', body: '', cta: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchEntry = async () => {
    const { data } = await supabase
      .from('cms_content')
      .select('*')
      .eq('page', activePage)
      .eq('section', activeSection)
      .eq('language', activeLang)
      .maybeSingle();
    if (data) {
      setForm({ title: '', subtitle: '', body: '', cta: '', ...(data.content as Record<string, string>) });
    } else {
      setForm({ title: '', subtitle: '', body: '', cta: '' });
    }
  };

  const fetchAllEntries = async () => {
    const { data } = await supabase.from('cms_content').select('id, page, section, language, updated_at, content').eq('language', activeLang).order('updated_at', { ascending: false });
    setEntries((data as CmsEntry[]) ?? []);
  };

  useEffect(() => { fetchEntry(); }, [activeLang, activePage, activeSection]);
  useEffect(() => { fetchAllEntries(); }, [activeLang]);

  const handleSave = async () => {
    setSaving(true);
    const payload = { page: activePage, section: activeSection, language: activeLang, content: form, updated_at: new Date().toISOString() };
    await supabase.from('cms_content').upsert([payload], { onConflict: 'page,section,language' });
    setSaving(false);
    setSaved(true);
    fetchAllEntries();
    setTimeout(() => setSaved(false), 2000);
  };

  const sections = SECTIONS[activePage] ?? [];

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1B2838]">CMS Content</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage Pashto and Dari content manually</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor */}
          <div className="lg:col-span-2 space-y-4">
            {/* Language Tabs */}
            <div className="bg-white rounded-xl border border-gray-100 p-1 flex gap-1 w-fit">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setActiveLang(lang.code)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${activeLang === lang.code ? 'bg-[#1B2838] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
            </div>

            {/* Page + Section Selectors */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Page</label>
                  <select value={activePage} onChange={e => { setActivePage(e.target.value); setActiveSection(SECTIONS[e.target.value]?.[0] ?? 'hero'); }} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] cursor-pointer capitalize">
                    {PAGES.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Section</label>
                  <select value={activeSection} onChange={e => setActiveSection(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] cursor-pointer capitalize">
                    {sections.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                {['title', 'subtitle', 'body', 'cta'].map(field => (
                  <div key={field}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 capitalize">{field}</label>
                    {field === 'body' ? (
                      <textarea
                        value={form[field] ?? ''}
                        onChange={e => setForm({ ...form, [field]: e.target.value })}
                        rows={5}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
                        placeholder={`Enter ${field} in ${activeLang === 'ps' ? 'Pashto' : 'Dari'}...`}
                        dir="rtl"
                      />
                    ) : (
                      <input
                        value={form[field] ?? ''}
                        onChange={e => setForm({ ...form, [field]: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                        placeholder={`Enter ${field} in ${activeLang === 'ps' ? 'Pashto' : 'Dari'}...`}
                        dir="rtl"
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                {saved && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> Saved!
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                >
                  {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Content
                </button>
              </div>
            </div>
          </div>

          {/* Recent Entries */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h2 className="text-sm font-bold text-[#1B2838] mb-4">
              Recent {activeLang === 'ps' ? 'Pashto' : 'Dari'} Entries
            </h2>
            {entries.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2"><i className="ri-file-text-line text-2xl"></i></div>
                <p className="text-xs">No content saved yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.map(entry => (
                  <button
                    key={entry.id}
                    onClick={() => { setActivePage(entry.page); setActiveSection(entry.section); }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="text-xs font-semibold text-[#1B2838] capitalize">{entry.page} / {entry.section}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{new Date(entry.updated_at).toLocaleDateString()}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
